import { useCallback } from "react";

import type { VideoDto } from "@/api/types";
import type { FormVideo } from "@/components/VideoInput/useVideoInput";
import { isVideoUploaded } from "@/components/VideoInput/useVideoInput";
import { fileMutations } from "@/queries/files/mutations";

export function useUploadVideo({
  onProgress,
  shouldProcess,
}: {
  onProgress?: ({ name, progress }: { name: string; progress: number }) => void;
  shouldProcess?: boolean;
}): {
  uploadFormVideo: UploadFormVideo;
  abortUploadVideos: AbortUploadVideos;
  isUploadingVideo: boolean;
} {
  const { mutateAsync: uploadVideo, isPending: isUploadingVideo } = fileMutations.useAddVideo({
    onProgress,
    shouldProcess,
  });

  const uploadFormVideo: UploadFormVideo = useCallback(
    async (formVideo) => {
      if (isVideoUploaded(formVideo)) {
        return formVideo;
      }

      if (formVideo.uploadPromise) {
        return formVideo.uploadPromise;
      }

      return await uploadVideo({
        ...formVideo,
        signal: formVideo.abortController?.signal,
      });
    },
    [uploadVideo],
  );

  const abortUploadVideos = (videos: FormVideo[]) => {
    videos.forEach((video) => {
      if (!isVideoUploaded(video)) {
        video.abortController?.abort();
        URL.revokeObjectURL(video.url);
      }
    });
  };

  return { uploadFormVideo, abortUploadVideos, isUploadingVideo };
}

type UploadFormVideo = (image: FormVideo) => Promise<VideoDto | undefined>;
type AbortUploadVideos = (videos: FormVideo[]) => void;
